import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFileOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import payableCreditsApi from '../../../api/payableCreditsApi';
import { applyServerErrors, getErrorMessage } from '../../../utils/errorHandler';

const schema = yup.object({
  description: yup.string().trim().nullable(),
  invoice_date: yup.string().required('Ingresa la fecha de facturación'),
  due_date: yup
    .string()
    .required('Ingresa el vencimiento de la letra')
    .test('after-invoice', 'Debe ser igual o posterior a la fecha de facturación', function (value) {
      const { invoice_date: invoiceDate } = this.parent;
      return !value || !invoiceDate || value >= invoiceDate;
    }),
  amount: yup
    .number()
    .typeError('Ingresa un monto válido')
    .positive('El monto debe ser mayor a 0')
    .required('Ingresa el monto'),
  currency: yup.string().oneOf(['PEN', 'USD']).required(),
  is_installment: yup.boolean().required(),
});

const emptyValues = {
  description: '',
  invoice_date: '',
  due_date: '',
  amount: '',
  currency: 'PEN',
  is_installment: false,
};

const formatMoney = (value, currency) =>
  new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'es-PE', { style: 'currency', currency: currency || 'PEN' }).format(
    value || 0
  );

let nextLocalId = -1;

// Modal de creacion/edicion de un credito por pagar (ej. compra a credito a un proveedor,
// con una letra). Si es a plazos, arma/edita la lista de cuotas; marcar una cuota como
// pagada (o el credito completo, si es al contado) es una accion aparte e inmediata, no
// forma parte de "Guardar" (ver payableCreditsApi.togglePaid / toggleInstallment).
export default function PayableCreditFormDialog({ open, onClose, onSaved, credit }) {
  const { enqueueSnackbar } = useSnackbar();
  const isEditing = Boolean(credit);

  const [installments, setInstallments] = useState([]);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [togglingPaid, setTogglingPaid] = useState(false);
  const [togglingInstallmentId, setTogglingInstallmentId] = useState(null);
  const [currentCredit, setCurrentCredit] = useState(credit);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: emptyValues,
  });

  const isInstallment = watch('is_installment');
  const currency = watch('currency');

  useEffect(() => {
    if (!open) {
      return;
    }
    setCurrentCredit(credit);
    setAttachmentFile(null);
    if (credit) {
      reset({
        description: credit.description || '',
        invoice_date: credit.invoice_date,
        due_date: credit.due_date,
        amount: credit.amount,
        currency: credit.currency,
        is_installment: credit.is_installment,
      });
      setInstallments(
        (credit.installments || []).map((i) => ({ id: i.id, due_date: i.due_date, amount: i.amount, paid: i.paid }))
      );
    } else {
      reset(emptyValues);
      setInstallments([]);
    }
  }, [open, credit, reset]);

  const addInstallment = () => {
    setInstallments((prev) => [...prev, { id: nextLocalId--, due_date: '', amount: '', paid: false, isNew: true }]);
  };

  const updateInstallment = (localId, field, value) => {
    setInstallments((prev) => prev.map((i) => (i.id === localId ? { ...i, [field]: value } : i)));
  };

  const removeInstallment = (localId) => {
    setInstallments((prev) => prev.filter((i) => i.id !== localId));
  };

  const handleToggleInstallment = async (installment) => {
    if (!currentCredit || installment.isNew) {
      return;
    }
    setTogglingInstallmentId(installment.id);
    try {
      const { data } = await payableCreditsApi.toggleInstallment(currentCredit.id, installment.id);
      setCurrentCredit(data.data);
      setInstallments(
        data.data.installments.map((i) => ({ id: i.id, due_date: i.due_date, amount: i.amount, paid: i.paid }))
      );
      onSaved();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo actualizar la cuota.'), { variant: 'error' });
    } finally {
      setTogglingInstallmentId(null);
    }
  };

  const handleTogglePaid = async () => {
    if (!currentCredit) {
      return;
    }
    setTogglingPaid(true);
    try {
      const { data } = await payableCreditsApi.togglePaid(currentCredit.id);
      setCurrentCredit(data.data);
      onSaved();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo actualizar el estado de pago.'), { variant: 'error' });
    } finally {
      setTogglingPaid(false);
    }
  };

  const handleAttachmentChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    if (!currentCredit) {
      // Todavia no existe el credito (se esta creando): se sube recien al guardar.
      setAttachmentFile(file);
      return;
    }
    setUploadingAttachment(true);
    try {
      const { data } = await payableCreditsApi.uploadAttachment(currentCredit.id, file);
      setCurrentCredit(data.data);
      onSaved();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo subir el archivo.'), { variant: 'error' });
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleRemoveAttachment = async () => {
    if (!currentCredit?.attachment_url) {
      setAttachmentFile(null);
      return;
    }
    setUploadingAttachment(true);
    try {
      const { data } = await payableCreditsApi.removeAttachment(currentCredit.id);
      setCurrentCredit(data.data);
      onSaved();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo quitar el archivo.'), { variant: 'error' });
    } finally {
      setUploadingAttachment(false);
    }
  };

  const onSubmit = async (data) => {
    if (data.is_installment && installments.length === 0) {
      enqueueSnackbar('Agrega al menos una cuota: el crédito es a plazos.', { variant: 'warning' });
      return;
    }

    const payload = {
      description: data.description || undefined,
      invoice_date: data.invoice_date,
      due_date: data.due_date,
      amount: data.amount,
      currency: data.currency,
      is_installment: data.is_installment,
      installments: data.is_installment
        ? installments.map((i) => ({ id: i.isNew ? undefined : i.id, due_date: i.due_date, amount: i.amount }))
        : [],
    };

    try {
      const response = currentCredit
        ? await payableCreditsApi.updatePayableCredit(currentCredit.id, payload)
        : await payableCreditsApi.createPayableCredit(payload);

      const savedCredit = response.data.data;

      if (!currentCredit && attachmentFile) {
        try {
          await payableCreditsApi.uploadAttachment(savedCredit.id, attachmentFile);
        } catch (error) {
          enqueueSnackbar(getErrorMessage(error, 'El crédito se guardó, pero no se pudo subir el archivo.'), {
            variant: 'warning',
          });
        }
      }

      enqueueSnackbar(currentCredit ? 'Crédito actualizado correctamente.' : 'Crédito registrado correctamente.', {
        variant: 'success',
      });
      onSaved();
      onClose();
    } catch (error) {
      const handled = applyServerErrors(error, setError);
      if (!handled) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudo guardar el crédito.'), { variant: 'error' });
      }
    }
  };

  const installmentsTotal = installments.reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
  const attachmentName = currentCredit?.attachment_original_name || attachmentFile?.name;
  const attachmentUrl = currentCredit?.attachment_url;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Editar crédito por pagar' : 'Nuevo crédito por pagar'}</DialogTitle>

      <DialogContent dividers>
        <Stack component="form" id="payable-credit-form" spacing={3} onSubmit={handleSubmit(onSubmit)} noValidate>
          <TextField
            fullWidth
            label="Proveedor / descripción"
            placeholder="Ej. Proveedor ABC - factura 001"
            {...register('description')}
          />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="date"
                label="Fecha de facturación"
                slotProps={{ inputLabel: { shrink: true } }}
                error={Boolean(errors.invoice_date)}
                helperText={errors.invoice_date?.message}
                {...register('invoice_date')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="date"
                label="Vencimiento de la letra"
                slotProps={{ inputLabel: { shrink: true } }}
                error={Boolean(errors.due_date)}
                helperText={errors.due_date?.message}
                {...register('due_date')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Monto"
                slotProps={{ htmlInput: { step: '0.01', min: 0 } }}
                error={Boolean(errors.amount)}
                helperText={errors.amount?.message}
                {...register('amount')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <TextField {...field} select fullWidth label="Moneda">
                    <MenuItem value="PEN">Soles (S/)</MenuItem>
                    <MenuItem value="USD">Dólares (US$)</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
          </Grid>

          {!isInstallment && currentCredit && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={currentCredit.status === 'pagado'}
                  onChange={handleTogglePaid}
                  disabled={togglingPaid}
                />
              }
              label={currentCredit.status === 'pagado' ? 'Pagado' : 'Marcar como pagado'}
            />
          )}

          <Divider />

          <Stack spacing={1.5}>
            <Controller
              name="is_installment"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="Pago a plazos (cuotas)"
                />
              )}
            />

            {isInstallment && (
              <Stack spacing={1.5}>
                {errors.installments?.message && <Alert severity="error">{errors.installments.message}</Alert>}
                <Typography variant="caption" color="text.secondary">
                  El checklist (pagado/pendiente) se marca al toque; las fechas/montos se guardan al hacer clic en
                  "Guardar".
                </Typography>

                {installments.length === 0 ? (
                  <Alert severity="info">Aún no agregaste cuotas.</Alert>
                ) : (
                  <List disablePadding>
                    {installments.map((installment, index) => (
                      <ListItem
                        key={installment.id}
                        divider
                        disableGutters
                        secondaryAction={
                          <IconButton size="small" onClick={() => removeInstallment(installment.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', width: '100%', pr: 5 }}>
                          <Checkbox
                            checked={installment.paid}
                            disabled={installment.isNew || togglingInstallmentId === installment.id}
                            onChange={() => handleToggleInstallment(installment)}
                          />
                          <Typography variant="body2" sx={{ width: 24 }}>
                            #{index + 1}
                          </Typography>
                          <TextField
                            type="date"
                            size="small"
                            label="Vencimiento"
                            slotProps={{ inputLabel: { shrink: true } }}
                            value={installment.due_date || ''}
                            onChange={(e) => updateInstallment(installment.id, 'due_date', e.target.value)}
                          />
                          <TextField
                            type="number"
                            size="small"
                            label="Monto"
                            slotProps={{ htmlInput: { step: '0.01', min: 0 } }}
                            value={installment.amount}
                            onChange={(e) => updateInstallment(installment.id, 'amount', e.target.value)}
                          />
                        </Stack>
                      </ListItem>
                    ))}
                  </List>
                )}

                <Stack sx={{ justifyContent: 'space-between', alignItems: 'center' }} direction="row">
                  <Button size="small" startIcon={<AddIcon />} onClick={addInstallment}>
                    Agregar cuota
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Suma de cuotas: {formatMoney(installmentsTotal, currency)}
                  </Typography>
                </Stack>
              </Stack>
            )}
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle2">Factura / letra del proveedor (foto o PDF)</Typography>
            {attachmentName ? (
              <Stack sx={{ alignItems: 'center' }} direction="row" spacing={1}>
                {attachmentUrl ? (
                  <Button size="small" href={attachmentUrl} target="_blank" rel="noopener">
                    {attachmentName}
                  </Button>
                ) : (
                  <Typography variant="body2">{attachmentName} (se subirá al guardar)</Typography>
                )}
                <IconButton size="small" onClick={handleRemoveAttachment} disabled={uploadingAttachment}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ) : (
              <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={<AttachFileIcon />}
                disabled={uploadingAttachment}
                sx={{ alignSelf: 'flex-start' }}
              >
                {uploadingAttachment ? 'Subiendo...' : 'Subir archivo'}
                <input type="file" hidden accept="image/*,.pdf" onChange={handleAttachmentChange} />
              </Button>
            )}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button type="submit" form="payable-credit-form" variant="contained" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

PayableCreditFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  credit: PropTypes.object,
};

PayableCreditFormDialog.defaultProps = {
  credit: null,
};
